// Dates, times and prices as a Swiss studio shows them: the visitor's language
// with Swiss conventions (fr-CH, de-CH, en-CH), 24-hour clock, studio time zone.
import { toBookingLocale } from "./locale";

export type BookingFormatters = ReturnType<typeof createFormatters>;

export function createFormatters(locale: string, timeZone: string, currency: string) {
  const intlLocale = `${toBookingLocale(locale)}-CH`;
  const dt = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(intlLocale, { timeZone, ...options });

  const time = dt({ hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const weekdayShort = dt({ weekday: "short" });
  const dayNumber = dt({ day: "numeric" });
  const dayLong = dt({ weekday: "long", day: "numeric", month: "long" });
  const dayShort = dt({ weekday: "short", day: "numeric", month: "short" });
  const range = dt({ day: "numeric", month: "long" });
  const dateLong = dt({ day: "numeric", month: "long", year: "numeric" });

  const money = (fractionDigits: number) =>
    new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  const whole = money(0);
  const cents = money(2);

  return {
    /** 18:30 */
    time: (date: Date | string) => time.format(new Date(date)),
    /** Mon */
    weekdayShort: (date: Date | string) => stripDot(weekdayShort.format(new Date(date))),
    /** 2 */
    dayNumber: (date: Date | string) => dayNumber.format(new Date(date)),
    /** Monday 2 November */
    dayLong: (date: Date | string) => dayLong.format(new Date(date)),
    /** Mon 2 Nov */
    dayShort: (date: Date | string) => dayShort.format(new Date(date)),
    /** 30 August */
    dayMonth: (date: Date | string) => range.format(new Date(date)),
    /** 30 August 2027 */
    dateLong: (date: Date | string) => dateLong.format(new Date(date)),
    /** 2 – 8 November */
    dateRange: (from: Date | string, to: Date | string) =>
      range.formatRange(new Date(from), new Date(to)),
    /** CHF 25, or CHF 22.50 when there are cents. */
    price: (amount: number) =>
      (Number.isInteger(amount) ? whole : cents).format(amount),
  };
}

// "Mo." / "lun." → "Mo" / "lun": the day strip has no room for the dot.
function stripDot(label: string) {
  return label.replace(/\.$/, "");
}

export type ValidityUnit = "day" | "week" | "month" | "year";

/**
 * Pass validity in the unit a person would say: 365 → 1 year, 180 → 6 months,
 * 14 → 2 weeks, 10 → 10 days. Studios count a month as 30 days.
 */
export function validityParts(days: number): { unit: ValidityUnit; n: number } {
  if (days >= 365 && days % 365 === 0) return { unit: "year", n: days / 365 };
  if (days >= 30 && days % 30 === 0) return { unit: "month", n: days / 30 };
  if (days >= 7 && days % 7 === 0) return { unit: "week", n: days / 7 };
  return { unit: "day", n: days };
}
